import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AddressRecord } from '@navio/nsdk';
import { URLKeys, noddiAsync } from 'noddi-async';
import { useAppForm } from 'noddi-ui-web';

import { emailRegex, isPhoneNumberValid } from 'noddi-util';
import { tracking, useAuthContext } from 'noddi-web';
import { z } from 'zod';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';

const getValidationSchema = () =>
  z.object({
    email: z
      .string()
      .min(1, t`This field is required`)
      .regex(emailRegex, t`Must provide a valid email address`),
    phoneNumber: z
      .string()
      .min(1, t`Required`)
      .refine((value) => isPhoneNumberValid(value ?? ''), t`Phone number is not valid`)
  });

type FormValues = z.input<ReturnType<typeof getValidationSchema>>;

interface NoServiceOfferedFormProps {
  address: AddressRecord;
  setIsFormSubmitted: (isFormSubmitted: boolean) => void;
}
export const NoServiceOfferedForm = ({ address, setIsFormSubmitted }: NoServiceOfferedFormProps) => {
  const { userData } = useAuthContext();

  const { mutateAsync: sendDeliveryNotification, error: sendDeliveryNotificationError } = noddiAsync.usePost({
    type: URLKeys.postDeliveryNotificationRequest,
    queryConfig: {
      onSuccess: () => {
        setIsFormSubmitted(true);
        tracking.track('noServiceOfferedLead', { hasAddress: Boolean(address) });
      }
    }
  });

  const defaultValues: FormValues = {
    email: userData?.user.email ?? '',
    phoneNumber: userData?.user.phoneNumber ?? ''
  };

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: getValidationSchema()
    },
    onSubmit: async ({ value }) => {
      await sendDeliveryNotification({
        ...value,
        addressId: address.id
      });
    }
  });

  return (
    <form.AppForm>
      <form
        className='flex flex-col gap-4'
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <h2>
          <Trans>
            If you want to be notified when we're in your area, enter your details below and we'll keep you updated!
          </Trans>
        </h2>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
          <form.AppField name='email'>{(field) => <field.Input label={t`Email`} type='email' />}</form.AppField>
          <form.AppField name='phoneNumber'>{(field) => <field.PhoneInput label={t`Phone number`} />}</form.AppField>
        </div>

        {sendDeliveryNotificationError && <ApiErrorMessageWithTrans error={sendDeliveryNotificationError} />}

        <div className='flex justify-end'>
          <form.SubscribeButton variant='outline' label={t`Notify Me`} />
        </div>
      </form>
    </form.AppForm>
  );
};
